import React, { useState, useEffect } from "react";

import { Panel, Th, Eyebrow, Lead } from "./Panel";
import CircularLoader from "../../CircularLoader/CircularLoader";
import { saveInvoiceDescription } from "../writes/workbenchWrites";
import { convertTime } from "../../../helpers/helpers";
import { useTranslation } from "react-i18next";

/* Production keeps the note draft and the admin invoice ID in ONE component,
   and a successful ID save empties the note draft while leaving its Save
   visible (AdminActions.jsx:101). The two live in separate panels here, so
   AdminControlsPanel calls clearNoteDraft(shipmentId) at that point. Keyed by
   shipment so a response that lands after the invoice was closed and another
   one opened cannot empty that other shipment's draft. */
const draftResetListeners = new Set();
export const clearNoteDraft = (shipmentId) =>
  draftResetListeners.forEach((listener) => {
    if (listener.shipmentId === shipmentId) listener.reset();
  });

/**
 * Admin shipment notes — internal, and the only note surface with a composer.
 *
 * Deliberately NOT the same panel as price revision notes. Those are anchored
 * to a charge revision, explain why an amount moved, and production tells the
 * admin they reach the customer by email. These are shipment-level and
 * internal. Merging the two risks routing an internal note into the customer
 * email path, and loses the revision anchor that a billing dispute depends on.
 * Revision notes live in <RevisionNotesPanel/>, beside the charges.
 *
 * Customer notes (shipment_notes where type === "user", which production shows
 * read-only in the "By User" block) are intentionally out of scope for now.
 *
 * The composer writes `invoice_description` alone to manage-invoice and reads
 * `shipment_notes` back off the response, exactly as production does. The
 * text is sent RAW (no trim) and the Save gate is production's, so blank,
 * whitespace-only and "cleared" drafts are sent exactly when production sends
 * them. See COVERAGE.md.
 */
const NotesPanel = ({ data, setData, setShipments, setSnack }) => {
  const { t } = useTranslation("admin");
  const [draft, setDraft] = useState("");
  const [isModified, setIsModified] = useState(false);
  const [saving, setSaving] = useState(false);

  const shipmentId = data?.id;
  useEffect(() => {
    const listener = { shipmentId, reset: () => setDraft("") };
    draftResetListeners.add(listener);
    return () => draftResetListeners.delete(listener);
  }, [shipmentId]);

  const snack = (text, severity) =>
    setSnack?.((prev) => ({ ...prev, open: true, text, severity }));

  // Production's admin-track filter. `category === "description"` catches the
  // row AdminActions synthesises client-side from invoice_description
  // (AdminActions.jsx:382-404). That row is display-only and never sent; it is
  // derived here instead of being written into data.
  const storedNotes = data?.shipment_notes || [];
  const lastNote = storedNotes.length
    ? storedNotes[storedNotes.length - 1]
    : null;
  const withDescription =
    typeof data?.invoice_description === "string" &&
    data.invoice_description.trim() &&
    lastNote?.category !== "description"
      ? [
          ...storedNotes,
          { notes: data.invoice_description, category: "description" },
        ]
      : storedNotes;
  const notes = withDescription.filter(
    (note) => note?.type === "admin" || note?.category === "description",
  );

  // Save gate copied verbatim from Classic AdminActions.jsx:63-72, see
  // COVERAGE.md. Compared against the stored invoice_description at keystroke
  // time and held in state; a missing key (undefined) shows Save for every
  // value, "" included.
  const handleDescriptionChange = (event) => {
    const newDescription = event.target.value;
    const isDescriptionModified =
      (data?.invoice_description !== null &&
        newDescription !== data?.invoice_description) ||
      (data?.invoice_description === null && newDescription !== "");
    setDraft(newDescription);
    setIsModified(isDescriptionModified);
  };

  // matches Classic handleAdminNotes, AdminActions.jsx:144-212.
  const handleSave = async () => {
    setSaving(true);
    const response = await saveInvoiceDescription(data?.id, draft);
    setSaving(false);

    if (response?.error) {
      snack(t("common:messages.somethingWentWrong"), "error");
      return;
    }

    snack(t("invoice.notesUpdatedSuccessfully"), "success");
    setIsModified(false);
    setDraft("");
    setData((prev) => ({
      ...prev,
      shipment_notes: response?.data?.result?.shipment_notes,
    }));
    setShipments?.((prev) => ({
      ...prev,
      data: prev?.data?.map((shipment) =>
        shipment.id === data.id
          ? {
              ...shipment,
              shipment_notes: response?.data?.result?.shipment_notes,
            }
          : shipment,
      ),
    }));
  };

  return (
    <Panel
      title={t("invoice.adminNotes")}
      chip={notes.length || null}
      hint={t("invoice.internalToThisShipment")}
    >
      {notes.length > 0 && (
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-ui-border bg-ui-surface-sunken">
              <Th>{t("invoice.note")}</Th>
              <Th>{t("invoice.addedBy")}</Th>
              <Th>{t("invoice.createdAt")}</Th>
            </tr>
          </thead>
          <tbody className="font-manrope text-[11.5px] font-medium text-ui-text">
            {notes.map((note, index) => (
              <tr
                key={note?.id ?? index}
                className="border-b border-ui-border-subtle"
              >
                <td className="px-[9px] py-1 leading-snug">{note?.notes}</td>
                <td className="px-[9px] py-1 capitalize text-ui-text-muted">
                  {note?.type}
                </td>
                <td className="whitespace-nowrap px-[9px] py-1 text-ui-text-muted">
                  {convertTime(note?.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="flex flex-col gap-1.5 px-2.5 py-2">
        <Eyebrow>{t("invoice.addANote")}</Eyebrow>
        <textarea
          value={draft}
          onChange={handleDescriptionChange}
          placeholder={t("invoice.writeANote")}
          className="min-h-[48px] w-full resize-y rounded-[5px] border border-ui-border-strong bg-ui-surface px-[9px] py-1 font-manrope text-[12px] font-medium leading-[1.55] text-ui-text"
        />
        <span className="font-manrope text-[10.5px] font-medium leading-snug text-ui-text-muted">
          <Lead label={t("invoice.internal")}>
            {t("invoice.customerNoteHint")}
          </Lead>
        </span>
        {isModified && (
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-1.5 rounded-[5px] border border-state-success-solid bg-state-success-solid px-3 py-1.5 font-manrope text-[10.5px] font-semibold text-brand-contrast hover:bg-state-success-solid-hover disabled:opacity-50"
            >
              {saving ? <CircularLoader size={14} color="grey" /> : t("invoice.saveNote")}
            </button>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default NotesPanel;
